<template>
  <div class="pv-overlay" :class="{ 'pv-pier': site === 'rbxpier' }" @click.self="close">
    <div class="pv-modal pv-modal--wide bulk-refund">
      <button class="pv-modal__close" @click="close" aria-label="Закрыть">×</button>
      <h2 class="pv-modal__title">Рефанд трансферов списком · {{ siteName }}</h2>

      <!-- Шаг 1: ввод списка -->
      <template v-if="step === 'input'">
        <p class="pv-modal__subtitle">
          Вставьте ID заказов в любом виде: столбиком, через запятую, из таблицы или прямо сообщением из Telegram. ID
          найдутся сами, дубли уберутся.
        </p>
        <textarea
          ref="idsInput"
          v-model="rawInput"
          class="pv-textarea pv-mono bulk-refund__input"
          :placeholder="placeholder"
        ></textarea>
        <div class="bulk-refund__counter">
          <span class="pv-badge" :class="parsed.ids.length ? 'pv-badge--ok' : 'pv-badge--muted'">
            Найдено ID: {{ parsed.ids.length }}
          </span>
          <span class="pv-badge pv-badge--muted" v-if="parsed.duplicates">Дублей убрано: {{ parsed.duplicates }}</span>
          <span class="pv-badge pv-badge--warn" v-if="parsed.junkLines">Строк без ID: {{ parsed.junkLines }}</span>
          <span class="pv-badge pv-badge--bad" v-if="parsed.ids.length > maxIds">
            Больше {{ maxIds }} — разбейте на части
          </span>
        </div>
        <div class="pv-actions bulk-refund__actions">
          <button class="pv-btn pv-btn--ghost" @click="close">Отмена</button>
          <button class="pv-btn pv-btn--primary" :disabled="!canCheck" @click="check">
            Проверить {{ parsed.ids.length || "" }} {{ plural(parsed.ids.length, "заказ", "заказа", "заказов") }}
          </button>
        </div>
      </template>

      <!-- Шаг 2: проверка -->
      <template v-if="step === 'check'">
        <p class="pv-modal__subtitle">
          <template v-if="isChecking">Проверяю заказы: {{ checkedCount }} из {{ rows.length }}…</template>
          <template v-else>
            Заказы, которые нельзя рефанднуть, сняты с выбора. Спорные — снимите или отметьте вручную.
          </template>
        </p>

        <div class="bulk-refund__progress" v-if="isChecking">
          <div class="bulk-refund__progress-bar" :style="{ width: `${(checkedCount / rows.length) * 100}%` }"></div>
        </div>

        <div class="bulk-refund__counter">
          <span class="pv-badge pv-badge--ok">К рефанду: {{ selectedRows.length }}</span>
          <span v-for="group in verdictGroups" :key="group.code" class="pv-badge" :class="`pv-badge--${group.tone}`">
            {{ group.label }}: {{ group.count }}
          </span>
        </div>

        <div class="bulk-refund__table-wrap">
          <table class="bulk-refund__table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    :checked="allSelected"
                    :disabled="isChecking || isRefunding"
                    @change="toggleAll"
                  />
                </th>
                <th>Order ID</th>
                <th>User ID</th>
                <th>Ник</th>
                <th>R$</th>
                <th>₽</th>
                <th>Дата</th>
                <th>Статус</th>
                <th>Вердикт</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.id" :class="{ 'is-off': row.verdict && !row.selected }">
                <td>
                  <input
                    type="checkbox"
                    v-model="row.selected"
                    :disabled="isRefunding || !row.verdict || !row.verdict.selectable"
                  />
                </td>
                <td class="pv-mono bulk-refund__copy" :title="row.id" @click="copy(row.id)">{{ row.id }}</td>
                <td
                  class="pv-mono bulk-refund__copy bulk-refund__user"
                  :title="row.order && row.order.user_id"
                  @click="copy(row.order && row.order.user_id)"
                >
                  {{ (row.order && row.order.user_id) || "—" }}
                </td>
                <td>{{ (row.order && row.order.username) || "—" }}</td>
                <td>{{ row.order && row.order.robux_amount != null ? row.order.robux_amount : "—" }}</td>
                <td>{{ rubOf(row.order) != null ? formatNumber(rubOf(row.order)) : "—" }}</td>
                <td class="bulk-refund__date">{{ row.order ? formatDate(row.order) : "—" }}</td>
                <td>
                  <span class="pv-badge pv-badge--muted" v-if="row.order">{{ statusOf(row) }}</span>
                  <span v-else>—</span>
                </td>
                <td>
                  <span class="pv-badge pv-badge--muted" v-if="row.refund === 'pending'">рефандю…</span>
                  <span class="pv-badge pv-badge--muted" v-else-if="!row.verdict">проверяю…</span>
                  <span class="pv-badge" :class="`pv-badge--${row.verdict.tone}`" :title="row.verdict.title" v-else>
                    {{ row.verdict.label }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="bulk-refund__footer" v-if="!isChecking">
          <div class="pv-summary bulk-refund__summary">
            <div class="pv-summary__row">
              <span>К рефанду</span>
              <span>
                <b>{{ selectedRows.length }}</b>
                {{ plural(selectedRows.length, "заказ", "заказа", "заказов") }} · {{ selectedUsers }}
                {{ plural(selectedUsers, "пользователь", "пользователя", "пользователей") }}
              </span>
            </div>
            <div class="pv-summary__row">
              <span>Сумма</span>
              <span>
                {{ formatNumber(selectedRobux) }} R$
                <template v-if="selectedRub !== null">· {{ formatNumber(selectedRub) }} ₽</template>
              </span>
            </div>
          </div>

          <div class="pv-section">
            <label class="pv-label" for="bulk-reason">
              Причина рефанда
              <span class="bulk-refund__required">*</span>
              — одна на всю пачку
            </label>
            <textarea
              id="bulk-reason"
              v-model="reason"
              class="pv-textarea"
              :class="{ 'is-invalid': reasonTouched && !reasonValid }"
              :disabled="isRefunding"
              placeholder="Например: сбой выдачи 09.10, робуксы не пришли"
            ></textarea>
            <p class="pv-hint is-error" v-if="reasonTouched && !reasonValid">Опишите причину — минимум 5 символов</p>
          </div>

          <!-- Выполнение -->
          <template v-if="isRefunding">
            <p class="pv-hint">Рефанд: {{ refundProgress.done }} из {{ refundProgress.total }}… Не закрывайте окно.</p>
            <div class="bulk-refund__progress">
              <div
                class="bulk-refund__progress-bar"
                :style="{ width: `${(refundProgress.done / refundProgress.total) * 100}%` }"
              ></div>
            </div>
          </template>

          <!-- Итог -->
          <div
            class="pv-alert"
            :class="refundSummary.failed ? 'pv-alert--warning' : 'pv-alert--success'"
            v-if="refundSummary"
          >
            Рефанднуто:
            <b>{{ refundSummary.refunded }}</b>
            <template v-if="formatMoney(refundSummary.amount)">
              &nbsp;на {{ formatMoney(refundSummary.amount) }}
            </template>
            <template v-if="refundSummary.failed">
              · ошибок:
              <b>{{ refundSummary.failed }}</b>
              (причины в таблице)
            </template>
          </div>
          <div class="pv-alert pv-alert--error" v-if="refundSummary && refundSummary.systemErrors">
            {{ refundSummary.systemErrors }}
            {{ plural(refundSummary.systemErrors, "заказ переведён", "заказа переведены", "заказов переведены") }} в
            system_error: статус уже сменён, но баланс мог не пополниться. Повторять нельзя — проверьте вручную и
            передайте Кириллу.
          </div>
          <div class="pv-alert pv-alert--error" v-for="error in refundErrors" :key="error">{{ error }}</div>

          <p class="pv-hint" v-if="!refundSummary && !isRefunding">
            Деньги вернутся на баланс пользователей. За один запрос — до {{ refundBatch }} заказов, больше отправим
            частями.
          </p>

          <div class="pv-actions bulk-refund__actions">
            <button class="pv-btn pv-btn--ghost" :disabled="isRefunding" @click="step = 'input'">
              ← Изменить список
            </button>
            <button class="pv-btn pv-btn--ghost" v-if="refundSummary" :disabled="isRefunding" @click="check">
              Перепроверить статусы
            </button>
            <button class="pv-btn pv-btn--ghost" :disabled="!selectedRows.length || isRefunding" @click="copySelected">
              Скопировать {{ selectedRows.length }} ID
            </button>
            <button class="pv-btn pv-btn--ghost" :disabled="isRefunding" @click="copyReport">Скопировать отчёт</button>
            <button
              class="pv-btn pv-btn--danger"
              :class="{ 'is-armed': refundArmed }"
              :disabled="!selectedRows.length || isRefunding"
              @click="startRefund"
            >
              <template v-if="isRefunding">Рефандю…</template>
              <template v-else-if="refundArmed">Подтвердить рефанд {{ selectedRows.length }}</template>
              <template v-else-if="refundSummary && refundSummary.failed">
                Повторить ошибочные ({{ selectedRows.length }})
              </template>
              <template v-else>
                Рефанднуть {{ selectedRows.length }} {{ plural(selectedRows.length, "заказ", "заказа", "заказов") }}
              </template>
            </button>
          </div>
        </div>
      </template>

      <transition name="toast-fade">
        <div class="bulk-refund__toast" v-if="toast">{{ toast }}</div>
      </transition>
    </div>
  </div>
</template>

<script>
import "@/scss/_privileged.scss";
import "./bulkRefund.scss";

import { SITE_NAMES, REFUND_BATCH, refundOrders, refundMessage, refundAmount } from "@/utils/privileged";

const OBJECT_ID_GLOBAL = /[a-f0-9]{24}/gi;
const MAX_IDS = 200;
const CONCURRENCY = 4;

// Где искать заказ. cancelledSmart — коллекция уже отменённых смарт-заказов.
const SOURCES = {
  rbxtree: [
    { kind: "transfer", url: "https://pending.backend-tree.com/get_history" },
    { kind: "smart", url: "https://pending.backend-tree.com/get_smart_history" },
    { kind: "cancelledSmart", url: "https://pending.backend-tree.com/get_cancelled_smart_history" }
  ],
  rbxpier: [{ kind: "transfer", url: "https://transfer.backend-pier.com/get_history" }]
};

// Предварительный отсев перед рефандом. Окончательно статус проверяет бэк (ответ «wrong status: …»).
function classify(order, kind) {
  if (!order) return { code: "notFound", label: "Не найден", tone: "bad", selectable: false, selected: false };
  if (kind === "cancelledSmart") {
    return { code: "cancelled", label: "Уже отменён", tone: "muted", selectable: false, selected: false };
  }
  // Смарт-заказы лежат в своей коллекции, у них отдельная отмена (cancel_smart_order)
  if (kind === "smart") {
    return { code: "smart", label: "Смарт — отменяется отдельно", tone: "muted", selectable: false, selected: false };
  }
  // Рефанд возможен только из success; error — деньги уже возвращены
  switch (order.status) {
    case "success":
      return { code: "ok", label: "Можно", tone: "ok", selectable: true, selected: true };
    case "error":
      return { code: "cancelled", label: "Уже возвращён", tone: "muted", selectable: false, selected: false };
    case "system_error":
      return { code: "systemError", label: "system_error — вручную", tone: "warn", selectable: false, selected: false };
    default:
      return {
        code: `status:${order.status}`,
        label: `Статус ${order.status || "—"} — нельзя`,
        tone: "bad",
        selectable: false,
        selected: false
      };
  }
}

export default {
  name: "BulkRefund",
  data() {
    const current = this.$store.state.toggleSite;
    return {
      site: SOURCES[current] ? current : "rbxtree",
      step: "input",
      rawInput: "",
      rows: [],
      isChecking: false,
      checkRun: 0,
      reason: "",
      reasonTouched: false,
      isRefunding: false,
      refundArmed: false,
      refundProgress: { done: 0, total: 0 },
      refundSummary: null,
      refundErrors: [],
      refundBatch: REFUND_BATCH,
      toast: null,
      maxIds: MAX_IDS,
      placeholder: "6ab23f74c6b6ff1b535e33d8\n6ab23f74c6b6ff1b535e33d9, 6ab23f74c6b6ff1b535e33da"
    };
  },
  computed: {
    siteName() {
      return SITE_NAMES[this.site];
    },
    parsed() {
      const matches = (this.rawInput.match(OBJECT_ID_GLOBAL) || []).map((id) => id.toLowerCase());
      const ids = [...new Set(matches)];
      const junkLines = this.rawInput
        .split(/\r?\n/)
        .filter((line) => line.trim() && !line.match(OBJECT_ID_GLOBAL)).length;
      return { ids, duplicates: matches.length - ids.length, junkLines };
    },
    reasonValid() {
      return this.reason.trim().length >= 5;
    },
    canCheck() {
      return this.parsed.ids.length > 0 && this.parsed.ids.length <= MAX_IDS;
    },
    checkedCount() {
      return this.rows.filter((row) => row.verdict).length;
    },
    selectedRows() {
      return this.rows.filter((row) => row.selected);
    },
    selectableRows() {
      return this.rows.filter((row) => row.verdict && row.verdict.selectable);
    },
    allSelected() {
      return this.selectableRows.length > 0 && this.selectableRows.every((row) => row.selected);
    },
    selectedUsers() {
      return new Set(this.selectedRows.map((row) => row.order && row.order.user_id).filter(Boolean)).size;
    },
    selectedRobux() {
      return this.selectedRows.reduce((sum, row) => sum + (Number(row.order && row.order.robux_amount) || 0), 0);
    },
    selectedRub() {
      const values = this.selectedRows.map((row) => this.rubOf(row.order));
      if (!values.length || values.some((value) => value == null)) return null;
      return values.reduce((sum, value) => sum + value, 0);
    },
    verdictGroups() {
      const groups = {};
      this.rows.forEach((row) => {
        if (!row.verdict || row.verdict.code === "ok") return;
        const { code, label, tone } = row.verdict;
        groups[code] = groups[code] || { code, label, tone, count: 0 };
        groups[code].count++;
      });
      return Object.values(groups);
    }
  },
  methods: {
    async check() {
      if (!this.canCheck) return;
      this.step = "check";
      this.isChecking = true;
      this.refundSummary = null;
      this.refundErrors = [];
      this.refundArmed = false;
      const run = ++this.checkRun;

      this.rows = this.parsed.ids.map((id) => ({
        id,
        order: null,
        kind: null,
        verdict: null,
        selected: false,
        refund: null
      }));

      const queue = [...this.rows];
      const worker = async () => {
        while (queue.length && run === this.checkRun) {
          const row = queue.shift();
          await this.checkRow(row);
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));

      if (run === this.checkRun) this.isChecking = false;
    },
    async checkRow(row) {
      const query = new URLSearchParams({
        count: 1,
        page: 1,
        is_admin: true,
        admin_key: this.$store.state.admin_key,
        order_id: row.id
      });

      const results = await Promise.all(
        SOURCES[this.site].map(async (source) => {
          try {
            const response = await fetch(`${source.url}?${query}`);
            if (!response.ok) return { kind: source.kind, failed: true };
            const data = await response.json();
            const order = (data.orders || []).find((item) => String(item._id).toLowerCase() === row.id);
            return { kind: source.kind, order };
          } catch (error) {
            return { kind: source.kind, failed: true };
          }
        })
      );

      // Отменённый смарт важнее обычной записи: заказ мог переехать в коллекцию отменённых
      const found =
        results.find((result) => result.order && result.kind === "cancelledSmart") ||
        results.find((result) => result.order);

      if (!found && results.every((result) => result.failed)) {
        row.verdict = { code: "failed", label: "Не удалось проверить", tone: "warn", selectable: false };
        return;
      }

      row.order = found ? found.order : null;
      row.kind = found ? found.kind : null;
      row.verdict = classify(row.order, row.kind);
      row.selected = row.verdict.selected;
    },
    // Первый клик взводит кнопку, второй (в течение 5 секунд) — запускает рефанд
    startRefund() {
      this.reasonTouched = true;
      if (!this.reasonValid || !this.selectedRows.length || this.isRefunding) return;
      if (!this.refundArmed) {
        this.refundArmed = true;
        clearTimeout(this.armTimer);
        this.armTimer = setTimeout(() => {
          this.refundArmed = false;
        }, 5000);
        return;
      }
      clearTimeout(this.armTimer);
      this.refundArmed = false;
      this.runRefund();
    },
    async runRefund() {
      const rows = this.selectedRows;
      const reason = this.reason.trim();
      this.isRefunding = true;
      this.refundErrors = [];
      this.refundProgress = { done: 0, total: rows.length };
      const summary = { refunded: 0, failed: 0, systemErrors: 0, amount: {} };

      for (let start = 0; start < rows.length; start += REFUND_BATCH) {
        const batch = rows.slice(start, start + REFUND_BATCH);
        batch.forEach((row) => {
          row.refund = "pending";
        });

        const result = await refundOrders(this.site, { orderIds: batch.map((row) => row.id), reason });

        if (!result.ok) {
          // Пачка целиком без ответа: строки остаются выбранными для повтора, бэк сам отсечёт уже рефанднутые
          this.refundErrors.push(result.message);
          batch.forEach((row) => this.markRefundFailed(row, { label: result.message }));
          summary.failed += batch.length;
          // Неверный ключ — режим выключен, остальные пачки не отправляем
          if (result.code === 401) {
            this.refundProgress.done += batch.length;
            break;
          }
        } else {
          const byId = {};
          result.data.results.forEach((item) => {
            byId[String(item.order_id).toLowerCase()] = item;
          });
          batch.forEach((row) => {
            const item = byId[row.id];
            if (item && item.status) {
              const amount = refundAmount(item);
              Object.entries(amount).forEach(([currency, value]) => {
                summary.amount[currency] = (summary.amount[currency] || 0) + (Number(value) || 0);
              });
              const sum = this.formatMoney(amount);
              row.refund = "ok";
              row.selected = false;
              row.verdict = {
                code: "refunded",
                label: sum ? `Рефанднут · ${sum}` : "Рефанднут",
                tone: "ok",
                selectable: false
              };
              summary.refunded++;
            } else {
              const message = refundMessage(item ? item.message : "нет в ответе сервера");
              this.markRefundFailed(row, message, item && item.message);
              summary.failed++;
              if (message.systemError) summary.systemErrors++;
            }
          });
        }
        this.refundProgress.done += batch.length;
      }

      this.refundSummary = summary;
      this.isRefunding = false;
    },
    // system_error не повторяем: статус уже сменён, деньги проверяют вручную
    markRefundFailed(row, { label, systemError }, raw) {
      row.refund = "failed";
      row.selected = row.selected && !systemError;
      row.verdict = {
        code: systemError ? "systemError" : "refundFailed",
        label,
        title: raw || label,
        tone: systemError ? "warn" : "bad",
        selectable: !systemError
      };
    },
    // { rub: 100.5, usd: 0, eur: 0 } → «100,5 ₽» (нулевые валюты не показываем)
    formatMoney(amount) {
      const signs = { rub: "₽", usd: "$", eur: "€" };
      return Object.entries(amount || {})
        .filter(([, value]) => Number(value))
        .map(([currency, value]) => `${this.formatNumber(value)} ${signs[currency] || currency.toUpperCase()}`)
        .join(" + ");
    },
    toggleAll() {
      const value = !this.allSelected;
      this.selectableRows.forEach((row) => {
        row.selected = value;
      });
    },
    statusOf(row) {
      if (row.kind === "cancelledSmart") return "smart · cancelled";
      if (row.kind === "smart") return row.order.configured ? "smart · configured" : "smart · unconfigured";
      return row.order.status || "—";
    },
    rubOf(order) {
      if (!order) return null;
      const value = order.rub_amount != null ? order.rub_amount : order.rubble_amount;
      return typeof value === "number" ? value : null;
    },
    formatNumber(value) {
      return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(value);
    },
    formatDate(order) {
      const value = order.date || order.datetime;
      return value ? this.$options.filters.formatDateRu(value) : "—";
    },
    plural(count, one, few, many) {
      const mod10 = count % 10;
      const mod100 = count % 100;
      if (mod10 === 1 && mod100 !== 11) return one;
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
      return many;
    },
    async copy(text) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        this.showToast("Скопировано");
      } catch (error) {
        this.showToast("Не удалось скопировать");
      }
    },
    copySelected() {
      this.copy(this.selectedRows.map((row) => row.id).join("\n"));
    },
    copyReport() {
      const lines = this.rows.map((row) => {
        const marks = { ok: "✅", failed: "❌" };
        const mark = marks[row.refund] || (row.selected ? "☑️" : "⛔");
        const verdict = row.verdict ? row.verdict.label : "не проверен";
        return `${mark} ${row.id} — ${verdict}`;
      });
      const header = this.refundSummary
        ? `${this.siteName}: рефанднуто ${this.refundSummary.refunded}, ошибок ${this.refundSummary.failed}` +
          (this.reason.trim() ? `\nПричина: ${this.reason.trim()}` : "")
        : `${this.siteName}: к рефанду ${this.selectedRows.length} из ${this.rows.length}`;
      this.copy([header, ...lines].join("\n"));
    },
    showToast(text) {
      this.toast = text;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toast = null;
      }, 1500);
    },
    close() {
      if (this.isRefunding) {
        this.showToast("Идёт рефанд — дождитесь окончания");
        return;
      }
      this.checkRun++;
      this.$store.state.isOpenBulkRefund = false;
    }
  },
  mounted() {
    this.$nextTick(() => this.$refs.idsInput && this.$refs.idsInput.focus());
  },
  beforeDestroy() {
    this.checkRun++;
    clearTimeout(this.toastTimer);
    clearTimeout(this.armTimer);
  }
};
</script>
